import { Component, EventEmitter, Output, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TripService } from '../../../../services/trip.service';
import { SubMenuService } from '../../../../services/submenu.service';
import { ClarityService } from '../../../../services/clarity.service';
import { SnackbarComponent } from '@predigle/micro/component-ui';

@Component({
  selector: 'app-active-trip',
  standalone: true,
  imports: [CommonModule, SnackbarComponent],
  templateUrl: './active-trip.component.html',
  styleUrl: './active-trip.component.scss'
})
export class ActiveTripComponent implements OnInit, OnDestroy {
  @Input() tripId: string = '';
  /**
   * Real trip start (ms epoch). Null falls back to "now", which is only correct for a
   * trip started in this tab — a trip started on the phone would otherwise show the
   * timer restarting from zero every time the web page loaded.
   */
  @Input() startTimeMs: number | null = null;
  @Output() viewDetails = new EventEmitter<void>();
  @Output() openVisibilitySettings = new EventEmitter<void>();
  @Output() inviteMate = new EventEmitter<void>();
  @Output() tripEnded = new EventEmitter<void>();

  isExpanded = false;
  timer = '00h 00m 00s';
  isPaused = false;

  // Snackbar state
  showSnackbar = false;
  snackbarMessage = '';
  snackbarType: 'success' | 'error' | 'warning' = 'success';

  private startTime: number = 0;
  private intervalId: any;
  private pausedTime: number = 0;

  constructor(private tripService: TripService, private submenuService: SubMenuService, private clarityService: ClarityService) { }

  ngOnInit() {
    this.startTime = this.startTimeMs ?? Date.now();
    this.startTimer();
  }

  ngOnDestroy() {
    this.stopTimer();
  }

  private startTimer() {
    this.intervalId = setInterval(() => {
      const now = Date.now();
      const diff = now - this.startTime; // Logic might need adjustment for resume, but start fresh is ok for now. 
      // Actually if we pause and resume, simply subtracting startTime isn't enough.
      // But for "Pause" requirement: "timer also should stop". 
      // User didn't ask for resume yet.
      this.timer = this.formatDuration(diff);
    }, 1000);
  }

  private stopTimer() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  onPause() {
    if (this.isPaused) return;

    this.stopTimer();
    this.isPaused = true;
    this.pausedAt = Date.now();

    if (this.tripId) {
      this.tripService.pauseTrip(this.tripId).subscribe({
        next: (res: any) => {
          console.log('Trip paused', res);
          this.clarityService.setTripState(true, 'paused');
          this.showSuccess('Trip paused successfully');
        },
        error: (err: any) => {
          console.error('Error pausing trip', err);
          this.showError('Error pausing trip');
        }
      });
    }
  }

  onResume() {
    if (!this.isPaused) return;

    const now = Date.now();
    const pauseDuration = now - this.pausedAt;
    this.startTime += pauseDuration;

    this.isPaused = false;
    this.startTimer();

    if (this.tripId) {
      this.tripService.resumeTrip(this.tripId).subscribe({
        next: (res: any) => {
          console.log('Trip resumed', res);
          this.clarityService.setTripState(true, 'active');
          this.showSuccess('Trip resumed successfully');
        },
        error: (err: any) => {
          console.error('Error resuming trip', err);
          this.showError('Error resuming trip');
        }
      });
    }
  }

  onStop() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.endTrip(position.coords.latitude, position.coords.longitude);
        },
        (err) => {
          console.error('Error getting location for end trip', err);
          this.showError('Could not get location. Ending with 0,0');
          this.endTrip(0, 0);
        }
      );
    } else {
      this.endTrip(0, 0);
    }
  }

  endTrip(lat: number, lng: number) {
    this.stopTimer();

    const payload = {
      end_position: {
        latitude: lat,
        longitude: lng
      },
      timer: this.timer
    };

    if (this.tripId) {
      this.tripService.stopTrip(this.tripId, payload).subscribe({
        next: (res: any) => {
          console.log('Trip stopped', res);

          // Calculate distance
          const startCoords = this.tripService.getStartCoords();
          let distance = 0;
          if (startCoords) {
            distance = this.calculateDistance(startCoords.lat, startCoords.lng, lat, lng);
            console.log(`Trip Distance: ${distance.toFixed(2)} km`);
            // Optionally show in snackbar or dialog?
            this.showSuccess(`Trip ended. Distance: ${distance.toFixed(2)} km`);
          } else {
            this.showSuccess('Trip ended successfully');
          }

          // Clear stored coords
          this.tripService.clearStartCoords();

          // "pop should disable" -> Emit event to parent to remove this component
          setTimeout(() => {
            this.tripEnded.emit();
          }, 1500);
        },
        error: (err: any) => {
          console.error('Error stopping trip', err);
          this.showError('Error stopping trip');
        }
      });
    }
  }

  // Basic Haversine formula for distance in km
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Radius of the earth in km
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance in km
    return d;
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  // --- SNACKBAR HELPERS ---
  private showSuccess(message: string) {
    this.snackbarMessage = message;
    this.snackbarType = 'success';
    this.showSnackbar = true;
  }

  private showError(message: string) {
    this.snackbarMessage = message;
    this.snackbarType = 'error';
    this.showSnackbar = true;
  }

  onSnackbarClose() {
    this.showSnackbar = false;
  }

  onSnackbarAutoClose() {
    this.showSnackbar = false;
  }

  // Need to track when we paused to calculate offset
  private pausedAt: number = 0;

  private formatDuration(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const hh = hours.toString().padStart(2, '0');
    const mm = minutes.toString().padStart(2, '0');
    const ss = seconds.toString().padStart(2, '0');

    return `${hh}h ${mm}m ${ss}s`;
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
    // Dismiss the modals that open from tapping a waypoint just to be safe when triggering auto-scrolling follow mode
    this.submenuService.closeWaypointPopup(null);
    this.submenuService.closeWaypointManager();
  }

  onViewFullDetails() {
    this.viewDetails.emit();
  }
}
